import { useMemo, useReducer } from 'react';

/** One step of the draft's history: its text, and where it's saved, if anywhere. */
interface Entry {
  value: string;
  place: string | null;
}

interface History {
  past: Entry[];
  value: string;
  /**
   * Where this text is saved, such as a draft in the account, as the host names it; null when
   * it's saved nowhere. Each step keeps its own, so Undo after opening a draft goes back to the
   * draft before and where that was saved, too.
   */
  place: string | null;
  future: Entry[];
  typedAt: number;
}

type Action =
  | { type: 'change'; value: string; typing: boolean; at: number; place?: string | null }
  | { type: 'reset'; value: string }
  | { type: 'save'; place: string | null; spread: boolean }
  | { type: 'undo' | 'redo' };

/**
 * Moves the steps next to the current one, saved where it was, as one run, to `place`: the
 * draft's history up to here, and on, where it now is.
 */
function spreadPlace(entries: Entry[], from: string | null, place: string | null): Entry[] {
  const at = entries.findIndex((entry) => entry.place !== from);
  const run = at === -1 ? entries.length : at;
  return [...entries.slice(0, run).map((entry) => ({ ...entry, place })), ...entries.slice(run)];
}

function reduce(state: History, action: Action): History {
  if (action.type === 'reset') {
    return { past: [], value: action.value, place: null, future: [], typedAt: 0 };
  }
  if (action.type === 'save') {
    if (!action.spread) return { ...state, place: action.place };
    return {
      ...state,
      // The past runs backwards from here.
      past: spreadPlace(state.past.toReversed(), state.place, action.place).toReversed(),
      place: action.place,
      future: spreadPlace(state.future, state.place, action.place),
    };
  }
  if (action.type === 'change') {
    const place = action.place === undefined ? state.place : action.place;
    if (action.value === state.value && place === state.place) return state;
    const merge =
      action.typing &&
      state.typedAt > 0 &&
      action.at - state.typedAt < 750 &&
      place === state.place;
    return {
      past: merge
        ? state.past
        : [...state.past, { value: state.value, place: state.place }].slice(-100),
      value: action.value,
      place,
      future: [],
      typedAt: action.typing ? action.at : 0,
    };
  }
  if (action.type === 'undo') {
    const previous = state.past.at(-1);
    if (previous === undefined) return state;
    return {
      past: state.past.slice(0, -1),
      ...previous,
      future: [{ value: state.value, place: state.place }, ...state.future],
      typedAt: 0,
    };
  }
  const next = state.future[0];
  if (next === undefined) return state;
  return {
    past: [...state.past, { value: state.value, place: state.place }],
    ...next,
    future: state.future.slice(1),
    typedAt: 0,
  };
}

/** Keep bulk edits reversible; group continuous typing into a single undo step. */
export function useDraftHistory(initial = '') {
  const [state, dispatch] = useReducer(reduce, {
    past: [],
    value: initial,
    place: null,
    future: [],
    typedAt: 0,
  });
  // Stable, so effects and callbacks that use them don't start over at every keystroke.
  const actions = useMemo(
    () => ({
      change: (value: string, typing = false) => {
        dispatch({ type: 'change', value, typing, at: Date.now() });
      },
      /**
       * Puts another draft in place, saved at `place`, or nowhere for null, as one edit that Undo
       * takes back, to the draft before.
       */
      replace: (value: string, place: string | null) => {
        dispatch({ type: 'change', value, typing: false, at: Date.now(), place });
      },
      /**
       * Notes that the draft is now saved at `place`. A draft saved nowhere till now takes its
       * whole run of history there; one saved elsewhere before, being copied, only this step.
       */
      save: (place: string | null, spread: boolean) => {
        dispatch({ type: 'save', place, spread });
      },
      undo: () => {
        dispatch({ type: 'undo' });
      },
      redo: () => {
        dispatch({ type: 'redo' });
      },
      /** Starts over with `value` and no history, as for a new piece of writing. */
      reset: (value: string) => {
        dispatch({ type: 'reset', value });
      },
    }),
    [],
  );
  return {
    value: state.value,
    /** Where the draft is saved, as the host named it; null when it's saved nowhere. */
    place: state.place,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
    ...actions,
  };
}
